import { useEffect, useMemo, useRef, useState } from "react";
import {
  CERTIFIED_LOGO_MAX_HEIGHT,
  CERTIFIED_LOGO_MAX_WIDTH,
} from "@shared/app-certification";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import { Logo } from "../_shared/Logo";
import { RemoteHint, type RemoteHintItem } from "../_shared/RemoteHint";
import { ImageSlot } from "../_shared/ImageSlot";
import { homeBgLayers } from "../_shared/bg";
import { useNow, formatDate, formatTime } from "../_shared/useNow";
import {
  useFocusGrid,
  useFocusNav,
  useScreenFocus,
  type NavMap,
} from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { useRadioNowPlaying } from "../_shared/useRadioNowPlaying";
import { useRadioPlayback } from "../_shared/useRadioPlayback";
import type { SchemeTokens } from "@shared/types/foyer";
import {
  SchemeProvider,
  useScheme,
  screenTitle,
  screenTitleOverride,
  visibleHomeSections,
  stationHue,
  type SchemeContextValue,
  type ResolvedApp,
} from "../_shared/schemeContext";
import { launchApp } from "../../lib/launchApp";
import { openTvForThisScreen } from "../../lib/openTv";
import { bridge } from "../../lib/foyerBridge";
import { castSteps } from "../../lib/casting";
import { SchemeSlot } from "../_shared/slots/SchemeSlot";
import CustomScreen from "../_shared/custom/CustomScreen";
import OffersEntry from "../_shared/offers/OffersEntry";
import VodScreen, { type VodScreenStyle } from "../_shared/vod/VodScreen";
import BuiltInPlayer from "../_shared/tv/BuiltInPlayer";
import GuideScreen from "../_shared/guide/GuideScreen";
import { useOfferSlot } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";
import { spectrumManifest } from "@shared/schemes/manifests";

// DEMO-1: TV + Screen Share, ported to Atelier/Mosaic/Spectrum from the
// launcher templates (`_shared/launcher/kit.tsx`), which is where these two
// constants normally live. Duplicated rather than imported - kit.tsx is the
// photo-forward launcher family's own file, and pulling a bespoke hotel
// scheme's TV tile out of it would tangle two template families that are
// deliberately independent.
//
// TV-6: that reasoning applied to the SCREEN-SHARE helper below, and still
// does. It never justified a fifth copy of the vendor app's package name, which
// is why the CMS's own `tvStreamingAppSlug` setting had no effect on any of
// them. Where the TV tile sends the screen - the vendor streaming app, or
// Foyer's own player when the premise has been switched to it - is now decided
// in one place, `lib/openTv.ts`, which every family calls.

/**
 * Open the device's own screen-sharing receiver; returns whether it took the
 * screen. `false` means there is nothing to open (a set-top box entered from
 * its own source list, the CMS preview) - the caller then shows instructions.
 * See `openScreenShare` in `_shared/launcher/kit.tsx` for the full reasoning.
 */
function tryOpenScreenShare(): boolean {
  const info = bridge.getCastingInfo();
  if (!info.supported || !info.launchable) return false;
  return bridge.launchCastReceiver();
}

/**
 * Spectrum - bold cinematic dark. Animated nebula ambient, gradient
 * text spans, glass cards with backdrop-blur, multi-layer focus glows,
 * hue-tinted radio, isometric 3D floor stack, weather chip. Ported
 * from `direction-spectrum.jsx`.
 */

/** Reconstruct Spectrum's palette object from resolved design tokens. Glass and
 *  glow surfaces live in token `extras`; there is no serif. */
function spectrumPalette(t: SchemeTokens) {
  const e = { ...spectrumManifest.defaultTokens.extras, ...t.extras };
  return {
    bg: t.palette.bg,
    ink: t.palette.ink,
    inkDim: t.palette.inkDim ?? "",
    inkSf: e.inkSf,
    rule: t.palette.rule ?? "",
    ruleSf: e.ruleSf,
    glass: e.glass,
    glassH: e.glassH,
    accent: t.palette.accent,
    accent2: t.palette.accent2 ?? "",
    glow: e.glow,
    sans: t.fonts.sans ?? "",
    mono: t.fonts.mono ?? "",
  };
}
function useS() {
  return spectrumPalette(useScheme().tokens);
}

type Screen =
  | "home"
  | "apps"
  | "info"
  | "radio"
  | "hotel"
  | "custom"
  | "offers"
  // Foyer's own player (TV-3a) - distinct from the "TV" tile's launch action.
  // Foyer's own television — the shell the guide and the library live inside.
  // The TV TILE is still a launch action; which application it opens is a
  // premise setting (`tvPlayer`). This is what it opens when that setting
  // names Foyer.
  | "tv"
  | "vod"
  | "guide"
  | "cast";

const FOOTER_KEYS: RemoteHintItem[] = [
  { key: "◄ ►", label: "Browse" },
  { key: "OK", label: "Open" },
  { key: "←", label: "Back" },
];

export default function SpectrumScheme({ scheme }: SchemeProps) {
  const [screen, setScreen] = useTrackedScreen<Screen>("home");
  // Which offer the home banner asked for, if any (`POS-4`) - see AtelierScheme
  // for why it is held beside the screen rather than inside it.
  const [offerId, setOfferId] = useState<string | null>(null);
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const go = (s: Screen) => {
    setOfferId(null);
    setScreen(s);
  };
  const openOffer = (id: string) => {
    setOfferId(id);
    setScreen("offers");
  };
  useBackHome(() => go("home"));
  const S = spectrumPalette(scheme.tokens);
  const { context } = scheme;
  // The "Hotel App" card stops being a dead end the moment there is a shop
  // behind it; with none it is exactly the screen it was.
  const hasOffers = (scheme.offers?.offers.length ?? 0) > 0;
  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: S.bg,
          color: S.ink,
          fontFamily: S.sans,
          outline: "none",
          overflow: "hidden",
        }}
      >
        <Ambient />
        {screen === "home" && (
          <Home
            go={go}
            openOffer={openOffer}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "apps" && (
          <Apps go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "info" && (
          <Info go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "radio" && (
          <Radio go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "custom" && (
          <SpectrumCustom go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "offers" && (
          <SpectrumOffers
            go={go}
            offerId={offerId}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "tv" && (
          <BuiltInPlayer
            style={{ ...playerStyle(S), bg: S.bg }}
            onExit={() => go("home")}
            scopeRef={scopeRef}
          />
        )}
        {screen === "vod" && (
          <SpectrumPlayer
            kind="vod"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "guide" && (
          <SpectrumPlayer
            kind="guide"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "hotel" &&
          (hasOffers ? (
            <SpectrumOffers go={go} scopeRef={scopeRef} context={context} />
          ) : (
            <HotelLaunch go={go} scopeRef={scopeRef} context={context} />
          ))}
        {screen === "cast" && (
          <Cast go={go} scopeRef={scopeRef} context={context} />
        )}
      </div>
    </SchemeProvider>
  );
}

function Ambient() {
  const S = useS();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "-30%",
          left: "-10%",
          width: "70%",
          height: "90%",
          background: `radial-gradient(closest-side, ${S.glow}, transparent 65%)`,
          filter: "blur(40px)",
          opacity: 0.55,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-30%",
          right: "-10%",
          width: "60%",
          height: "80%",
          background:
            "radial-gradient(closest-side, rgba(33,212,253,0.35), transparent 65%)",
          filter: "blur(60px)",
          opacity: 0.55,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
          mixBlendMode: "overlay",
        }}
      />
    </div>
  );
}

function Header({ crumb }: { crumb?: string }) {
  const S = useS();
  const now = useNow();
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${PAGE_Y}px ${PAGE_X}px 22px`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <Logo variant="light" height={22} />
        {crumb && (
          <>
            <span
              style={{ width: 1, height: 18, background: S.rule }}
              aria-hidden
            />
            <span
              style={{
                fontSize: 13,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: S.inkSf,
              }}
            >
              {crumb}
            </span>
          </>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          fontFamily: S.mono,
        }}
      >
        <WeatherChip />
        <span
          style={{
            fontSize: 12,
            color: S.inkSf,
            letterSpacing: "0.08em",
          }}
        >
          {formatDate(now).toUpperCase()}
        </span>
        <span
          style={{
            fontSize: 22,
            fontVariantNumeric: "tabular-nums",
            color: S.ink,
            letterSpacing: "-0.02em",
          }}
        >
          {formatTime(now)}
        </span>
      </div>
    </div>
  );
}

function WeatherChip() {
  const S = useS();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px 5px 6px",
        borderRadius: 999,
        background: S.glass,
        border: `0.5px solid ${S.rule}`,
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 35% 35%, #ffeaa7, #f6c844 60%, #b8852b)",
          boxShadow: "0 0 8px rgba(246,200,68,0.6)",
        }}
      />
      <span
        style={{
          fontSize: 11,
          color: S.ink,
          letterSpacing: "0.04em",
          fontFamily: S.mono,
        }}
      >
        4° · CLEAR
      </span>
    </div>
  );
}

function Footer({
  items,
  context,
}: {
  items: RemoteHintItem[];
  context: SchemeContextValue;
}) {
  const S = useS();
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: `14px ${PAGE_X}px ${PAGE_Y}px`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "linear-gradient(to top, rgba(10,13,24,0.85), transparent)",
        color: S.inkDim,
        zIndex: 5,
      }}
    >
      <RemoteHint
        items={items.map((i) => ({
          ...i,
          bg: "rgba(255,255,255,0.06)",
          fg: S.ink,
          border: "0.5px solid rgba(255,255,255,0.12)",
        }))}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: S.mono,
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: S.inkSf,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: S.accent2,
            boxShadow: `0 0 8px ${S.accent2}`,
          }}
        />
        <span>{context.hotelName}</span>
      </div>
    </div>
  );
}

interface ScreenProps {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}

/**
 * The premise's own page. The template lends the bar its accent and font and
 * owns nothing else - the operator's picture IS the page (see CustomScreen).
 */
function SpectrumCustom({ go, scopeRef }: ScreenProps) {
  const S = useS();
  return (
    <CustomScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      style={{
        barBg: "rgba(10,13,24,0.55)",
        ink: S.ink,
        accent: S.accent,
        onAccent: S.bg,
        font: S.sans,
      }}
    />
  );
}

/**
 * Room offers, in Spectrum's chrome: the shared renderer (see OffersScreen) on
 * glass, over the nebula, inside the template's own header and footer.
 */
function SpectrumOffers({
  go,
  scopeRef,
  context,
  offerId,
}: ScreenProps & { offerId?: string | null }) {
  const S = useS();
  const scheme = useScheme();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, "offers", "Offers")} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `0 ${PAGE_X}px 96px`,
        }}
      >
        <OffersEntry
          offerId={offerId}
          scopeRef={scopeRef}
          onBack={() => go("home")}
          title={screenTitleOverride(scheme, "offers")}
          pad={{ x: 0, y: 0 }}
          hints={false}
          style={{
            bg: "transparent",
            card: S.glass,
            ink: S.ink,
            inkDim: S.inkDim,
            rule: S.rule,
            accent: S.accent,
            onAccent: S.bg,
            titleFont: S.sans,
            font: S.sans,
            radius: 16,
            placeholder: S.glassH,
          }}
        />
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/**
 * Foyer's own player, in Spectrum's chrome (`TV-3a`): the shared renderers on
 * glass, over the nebula, inside the template's own header and footer. One
 * component for both, because the template's answer to both is the same set of
 * tokens.
 */

/**
 * This template's palette as a `VodScreenStyle` — the one shape every player
 * surface speaks (`VodScreen`, `GuideScreen`, `TvShell`).
 *
 * Named once because it is now read twice: by the inset sub-pages, which sit
 * on the template's photograph and so want `bg: "transparent"`, and by
 * `BuiltInPlayer`, which REPLACES that photograph and overrides `bg` with the
 * template's own surface.
 */
function playerStyle(S: ReturnType<typeof useS>): VodScreenStyle {
  return {
    bg: "transparent",
    card: S.glass,
    ink: S.ink,
    inkDim: S.inkDim,
    rule: S.rule,
    accent: S.accent,
    onAccent: S.bg,
    titleFont: S.sans,
    font: S.sans,
    radius: 16,
    placeholder: S.glassH,
  };
}

function SpectrumPlayer({
  kind,
  go,
  scopeRef,
  context,
}: ScreenProps & { kind: "vod" | "guide" }) {
  const S = useS();
  const scheme = useScheme();
  const fallback = kind === "vod" ? "Films & series" : "TV guide";
  const style = playerStyle(S);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, kind, fallback)} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `0 ${PAGE_X}px 96px`,
        }}
      >
        {kind === "vod" ? (
          <VodScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "vod")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        ) : (
          <GuideScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "guide")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        )}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/** The promo banner's id in the home d-pad graph. Not a screen id - it opens an
 *  offer, not a page, and must never collide with one. */
const PROMO_CELL = "homePromo";

function Home({
  go,
  openOffer,
  scopeRef,
  context,
}: ScreenProps & { openOffer: (offerId: string) => void }) {
  const S = useS();
  const scheme = useScheme();
  const clock = useNow();
  // A live offer in the promo slot earns a cell in the home d-pad chain.
  const banner = useOfferSlot(PROMO_CELL, clock);
  const hasBanner = !!banner;
  // Premise background image: the operator's hero photo fills the cinematic hero
  // slot (honouring content.background.mode - `color` falls back to the striped
  // placeholder). The gradient + text overlays below keep the copy legible.
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  // The dest cards sit in a row beneath the hero; the tall Hotel card is in the
  // right column. The hotel app is to the *right* of the last dest. The visible
  // dest set (toggles/order/content) drives both the cards and the D-pad chain:
  // a left↔right line over [visible dests…, hotel].
  const DEST_DEF: Record<
    string,
    { label: string; hint: string; color: string }
  > = {
    // TV is the fixed anchor - always first, never toggleable, same rule the
    // launcher templates and Care follow (DEMO-1).
    tv: { label: "TV", hint: "Live channels · the remote", color: S.accent },
    cast: {
      label: "Screen Share",
      hint: "Mirror a phone or laptop",
      color: S.accent2,
    },
    apps: { label: "Apps", hint: "12 streaming services", color: S.accent },
    info: {
      label: "Information",
      hint: "Wi-Fi · dining · the map",
      color: S.accent2,
    },
    radio: {
      label: "Radio",
      hint: "12 stations · DAB+ & online",
      color: "#ff6e9c",
    },
    // The premise's own page. `label` is the fallback only - the name comes
    // from the `customTitle` knob, applied below.
    custom: {
      label: "Custom",
      hint: "A page of our own",
      color: S.accent2 || S.accent,
    },
    // Room offers. Same rule: the name comes from `offersTitle`. A screen the
    // manifest lists in `homeOrder.items` and this table does not is a crash -
    // the loud version of a tile that opens nothing.
    offers: {
      label: "Offers",
      hint: "Order in · book the spa",
      color: "#ffb347",
    },
    // Foyer's own player. Same rule again: the names come from `vodTitle` /
    // `guideTitle`, applied below.
    // Hints kept SHORT: the dest cards share one `1fr` row, so every extra
    // destination narrows all of them.
    vod: {
      label: "Films & series",
      hint: "Watch here, now",
      color: "#8b5cf6",
    },
    guide: {
      label: "TV guide",
      hint: "What is on tonight",
      color: "#34d399",
    },
  };
  const visible = visibleHomeSections(scheme);
  // TV first always, then Screen Share when its toggle is on, then the
  // content-driven dests - same fixed-anchor rule as the launcher templates
  // (DEMO-1). Neither TV nor Screen Share is a member of `homeOrder`: like
  // Hotel, they are handled here rather than through the generic order knob.
  const showCast = scheme.knobs.has("showCast")
    ? scheme.knobs.toggle("showCast")
    : true;
  const anchorIds = showCast ? ["tv", "cast"] : ["tv"];
  const dests = [...anchorIds, ...visible].map((id) => ({
    id,
    ...DEST_DEF[id],
    label: screenTitle(scheme, id, DEST_DEF[id].label),
  }));
  const nav = useMemo<NavMap>(() => {
    const chain = [...anchorIds, ...visible, "hotel"];
    const map: NavMap = {};
    chain.forEach((id, i) => {
      map[id] = {};
      if (i > 0) map[id].left = chain[i - 1];
      if (i < chain.length - 1) map[id].right = chain[i + 1];
      // The banner is a glass overlay at the TOP of the hero, above the whole
      // card row, so Up reaches it from anywhere on that row and Down comes
      // back to where the ring started. Only when it holds a live offer: a
      // d-pad wall beats a cell that draws nothing.
      if (hasBanner) map[id].up = PROMO_CELL;
    });
    if (hasBanner) map[PROMO_CELL] = { down: chain[0] };
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.join("|"), anchorIds.join("|"), hasBanner]);
  // TV is a launch action, not a sub-page (DEMO-1) - same rule the launcher
  // templates follow. Screen Share tries the device's own receiver first and
  // only falls back to the instructions page when there is nothing to open.
  // Shared by the d-pad (`onEnter`) and the mouse (`onClick`) paths below.
  const pressDest = (id: string) => {
    if (id === "tv") {
      // `openBuiltin` is what gives the premise switch teeth: a premise on
      // `tvPlayer: "foyer"` opens our own player, one on "norigin" (the
      // default) still hands the screen to the vendor app.
      //
      // 🚨 It opens the PLAYER, not the guide. Dropping a guest straight into
      // the channel list is the behaviour `1fd8e91` replaced — "a channel
      // list, not a television: landing in one leaf of a structure with no way
      // to reach the others reads as a broken app rather than a smaller one".
      // Only the launcher kit was migrated then; this template kept the
      // superseded call, so one button meant two things depending on which
      // design a premise had chosen.
      openTvForThisScreen({
        openBuiltin: () => go("tv"),
        // The premise's choice from the config this render is using — the CMS
        // preview has one and no pairing binding to read a cache with.
        tvPlayer: scheme.context.tvPlayer,
      });
      return;
    }
    if (id === "cast") {
      if (tryOpenScreenShare()) return;
      go("cast");
      return;
    }
    go(id as Screen);
  };
  const [focus, setFocus] = useFocusNav({
    map: nav,
    // Still the first destination card. The banner is an offer nobody asked
    // for; it may interrupt the eye, never the remote.
    initial: dests[0]?.id ?? "hotel",
    scopeRef,
    // The promo cell is NOT a screen id, so it is handled before the cast and
    // never falls through to `go()`. A `go("homePromo")` matches no `screen ===`
    // branch and would leave the guest on an empty page.
    onEnter: (id) => {
      if (id === PROMO_CELL) {
        if (banner) openOffer(banner.offer.id);
        return;
      }
      pressDest(id);
    },
  });
  // The banner can go away under the ring - its window closes, the offer is
  // deleted, the feed comes back without it - and `useFocusNav` walls every
  // direction for an id that is no longer in the map. Pull focus back to the
  // first card rather than strand the remote on a cell that draws nothing.
  useEffect(() => {
    if (!hasBanner && focus === PROMO_CELL) {
      setFocus(dests[0]?.id ?? "hotel");
    }
  }, [hasBanner, focus, setFocus, dests]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 360px",
          gap: 28,
          padding: `0 ${PAGE_X}px 96px`,
          minHeight: 0,
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ImageSlot
            kind="hero film still · 21:9"
            tone="dark"
            radius={20}
            src={bgL.photo ? hero : undefined}
            style={{ flex: 1, minHeight: 0, position: "relative" }}
          >
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 20,
                  background:
                    "linear-gradient(160deg, rgba(124,108,255,0.18), rgba(33,212,253,0.10) 60%, rgba(0,0,0,0.4))",
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                left: PAGE_X,
                right: 28,
                bottom: 24,
                color: S.ink,
              }}
            >
              <div
                style={{
                  fontFamily: S.mono,
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: S.accent,
                  marginBottom: 10,
                }}
              >
                ▸ Tonight at the hotel
              </div>
              <h1
                style={{
                  margin: 0,
                  fontFamily: S.sans,
                  fontWeight: 600,
                  fontSize: 56,
                  lineHeight: 0.96,
                  letterSpacing: "-0.03em",
                }}
              >
                Welcome to a quieter{" "}
                <span
                  style={{
                    background: `linear-gradient(90deg, ${S.accent}, ${S.accent2})`,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  floor.
                </span>
              </h1>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 14,
                  color: S.inkDim,
                  maxWidth: 540,
                }}
              >
                Stream anything · order anything · explore the house - start by
                picking up the remote.
              </div>
            </div>
          </ImageSlot>
          {/* operator promo slot - glass overlay at the top of the hero */}
          <SchemeSlot
            slotId={PROMO_CELL}
            focused={focus === PROMO_CELL}
            onPress={openOffer}
            style={{
              bg: "rgba(16,18,40,0.82)",
              ink: S.ink,
              inkDim: S.inkDim,
              accent: S.accent,
              rule: S.rule,
              font: S.sans,
              kickerFont: S.mono,
              radius: 16,
            }}
            containerStyle={{
              position: "absolute",
              // 76, not 24: the hero carries its own caption in that corner, and
              // a promotion sitting on top of it read as a card floating in the
              // text rather than placed on the photo.
              top: 76,
              left: PAGE_X,
              width: 420,
              zIndex: 4,
            }}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(dests.length, 1)}, 1fr)`,
              gap: 14,
              marginTop: 18,
            }}
          >
            {dests.map((d) => {
              const active = focus === d.id;
              return (
                <button
                  key={d.id}
                  onMouseEnter={() => setFocus(d.id)}
                  onClick={() => pressDest(d.id)}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: "20px 22px",
                    textAlign: "left",
                    background: S.glass,
                    color: "inherit",
                    fontFamily: "inherit",
                    borderRadius: 16,
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: active
                      ? `0 0 0 1.5px ${d.color}, 0 0 28px -4px ${d.color}, inset 0 1px 0 rgba(255,255,255,0.06)`
                      : `inset 0 0 0 0.5px ${S.rule}, inset 0 1px 0 rgba(255,255,255,0.04)`,
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    transform: active ? "translateY(-2px)" : "none",
                    transition:
                      "transform 220ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 220ms",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      opacity: active ? 0.4 : 0.18,
                      background: `radial-gradient(closest-side at 80% 30%, ${d.color}, transparent 60%)`,
                    }}
                  />
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 600,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {d.label}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: S.inkDim,
                        marginTop: 2,
                      }}
                    >
                      {d.hint}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <button
          onMouseEnter={() => setFocus("hotel")}
          onClick={() => go("hotel")}
          style={{
            appearance: "none",
            border: 0,
            textAlign: "left",
            cursor: "pointer",
            color: S.ink,
            fontFamily: "inherit",
            borderRadius: 22,
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            background: `linear-gradient(180deg, ${S.accent} 0%, #3b2bb3 60%, #14101f 100%)`,
            boxShadow:
              focus === "hotel"
                ? `0 0 0 1.5px #fff, 0 18px 64px -8px ${S.glow}, inset 0 1px 0 rgba(255,255,255,0.18)`
                : "inset 0 1px 0 rgba(255,255,255,0.18), 0 12px 40px -8px rgba(124,108,255,0.4)",
            transition: "box-shadow 220ms, transform 220ms",
            transform:
              focus === "hotel" ? "translateY(-2px) scale(1.005)" : "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "24px 26px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: -40,
              background:
                "radial-gradient(closest-side at 30% 0%, rgba(255,255,255,0.4), transparent 50%)",
              filter: "blur(28px)",
              mixBlendMode: "overlay",
              pointerEvents: "none",
            }}
          />
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: S.mono,
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.75)",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />
              The hotel · concierge
            </div>
            <h2
              style={{
                margin: "12px 0 0",
                fontFamily: S.sans,
                fontSize: 56,
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontWeight: 600,
              }}
            >
              Hotel
              <br />
              App
            </h2>
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                color: "rgba(255,255,255,0.78)",
                lineHeight: 1.5,
                maxWidth: 280,
              }}
            >
              Order to the room · book the spa · request housekeeping · skip the
              front desk on check-out.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              position: "relative",
            }}
          >
            {[
              { t: "Order to the room", s: "24 h kitchen" },
              { t: "Book the spa", s: "Tomorrow · 10:00" },
              { t: "Late check-out", s: "€40 · until 16:00" },
              { t: "View the bill", s: "Live · €1 240,50" },
            ].map((it) => (
              <div
                key={it.t}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.10)",
                  border: "0.5px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(12px)",
                  fontSize: 12,
                }}
              >
                <span style={{ fontWeight: 500 }}>{it.t}</span>
                <span
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontFamily: S.mono,
                    fontSize: 11,
                  }}
                >
                  {it.s}
                </span>
              </div>
            ))}
          </div>
        </button>
      </div>
      <Footer items={FOOTER_KEYS.slice(0, 2)} context={context} />
    </div>
  );
}

function Apps({ go, scopeRef, context }: ScreenProps) {
  const S = useS();
  const { content } = useScheme();
  const items = [
    { id: "hotel", name: "Hotel App", isHotel: true as const },
    ...content.apps.map((a) => ({ ...a, isHotel: false as const })),
  ];
  const cols = 4;
  const rows = Math.ceil(items.length / cols);
  const [focus, setFocus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    onEnter: (f) => {
      const it = items[f.r * cols + f.c];
      if (!it) return;
      if (it.isHotel) go("hotel");
      else launchApp(it);
    },
    onBack: () => go("home"),
    wrap: false,
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Apps" />
      <div
        style={{
          flex: 1,
          padding: `0 ${PAGE_X}px 96px`,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: S.sans,
              fontWeight: 600,
              fontSize: 44,
              letterSpacing: "-0.02em",
            }}
          >
            Pick something to{" "}
            <span
              style={{
                background: `linear-gradient(90deg, ${S.accent}, ${S.accent2})`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              watch.
            </span>
          </h2>
          <div
            style={{
              display: "flex",
              gap: 8,
              fontFamily: S.mono,
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: S.inkSf,
            }}
          >
            <span>{items.length} apps</span>
            <span>·</span>
            <span>signed in to all</span>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            // `1fr` rows would stretch the cards and override their 16:9.
            gridAutoRows: "auto",
            alignContent: "start",
            gap: 14,
            minHeight: 0,
          }}
        >
          {items.map((it, i) => {
            const r = Math.floor(i / cols);
            const c = i % cols;
            const active = focus.r === r && focus.c === c;
            return (
              <AppCard
                key={it.id}
                app={it}
                active={active}
                onMouseEnter={() => setFocus({ r, c })}
                onClick={() => (it.isHotel ? go("hotel") : launchApp(it))}
              />
            );
          })}
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

interface AppCardProps {
  app:
    | { id: string; name: string; isHotel: true }
    | (ResolvedApp & { isHotel: false });
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

function AppCard({ app, active, onMouseEnter, onClick }: AppCardProps) {
  const S = useS();
  const isHotel = app.isHotel;
  const bg = isHotel
    ? `linear-gradient(135deg, ${S.accent}, #3b2bb3 80%)`
    : (app as ResolvedApp).color || "#222";
  return (
    <button
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        appearance: "none",
        border: 0,
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
        background: bg,
        color: isHotel ? "#fff" : (app as ResolvedApp).text || "#fff",
        position: "relative",
        // 16:9 - the frame the certification describes (NFLX-5).
        aspectRatio: "16 / 9",
        // border-box, or the ratio governs the CONTENT box and the padding is
        // added on top - which renders 1.65:1, not 16:9. Measured, not assumed.
        boxSizing: "border-box",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: active
          ? `0 0 0 1.5px ${S.ink}, 0 0 30px -2px rgba(255,255,255,0.25), 0 18px 32px -10px rgba(0,0,0,0.6)`
          : "inset 0 0 0 0.5px rgba(255,255,255,0.10), 0 6px 18px -6px rgba(0,0,0,0.5)",
        transform: active ? "translateY(-3px) scale(1.015)" : "none",
        transition:
          "transform 220ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 220ms",
        fontFamily: S.sans,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.6,
          background:
            "radial-gradient(closest-side at 30% 0%, rgba(255,255,255,0.18), transparent 60%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontFamily: S.mono,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              opacity: 0.7,
            }}
          >
            {isHotel ? "House · concierge" : "Streaming"}
          </span>
          {!isHotel && active && (
            <span
              style={{
                fontSize: 9,
                fontFamily: S.mono,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                padding: "3px 7px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                backdropFilter: "blur(8px)",
              }}
            >
              Press OK
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: isHotel ? 32 : 24,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: isHotel ? "#fff" : (app as ResolvedApp).text || "#fff",
          }}
        >
          {isHotel ? (
            "Hotel"
          ) : (app as ResolvedApp).logoUrl ? (
            <img
              src={(app as ResolvedApp).logoUrl}
              alt={app.name}
              style={{
                maxHeight: (app as ResolvedApp).certified
                  ? CERTIFIED_LOGO_MAX_HEIGHT
                  : 50,
                maxWidth: (app as ResolvedApp).certified
                  ? CERTIFIED_LOGO_MAX_WIDTH
                  : "90%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            (app as ResolvedApp).wordmark
          )}
        </div>
      </div>
    </button>
  );
}

function Info({ go, scopeRef, context }: ScreenProps) {
  const S = useS();
  const [tab, setTab] = useState<"directory" | "welcome">("directory");
  const tabs: ("directory" | "welcome")[] = ["directory", "welcome"];
  const [focus, setFocus] = useFocusGrid({
    rows: 1,
    cols: 2,
    scopeRef,
    onEnter: (f) => setTab(tabs[f.c]),
    onBack: () => go("home"),
  });
  useEffect(() => {
    setFocus({ r: 0, c: tabs.indexOf(tab) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Information" />
      <div
        style={{
          flex: 1,
          padding: `0 ${PAGE_X}px 96px`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: S.sans,
              fontWeight: 600,
              fontSize: 44,
              letterSpacing: "-0.02em",
            }}
          >
            The house, in <span style={{ color: S.accent }}>brief.</span>
          </h2>
          <div
            style={{
              display: "flex",
              gap: 6,
              padding: 4,
              background: S.glass,
              borderRadius: 999,
              border: `0.5px solid ${S.rule}`,
            }}
          >
            {tabs.map((t, i) => {
              const active = tab === t;
              const focused = focus.c === i;
              return (
                <button
                  key={t}
                  onMouseEnter={() => setFocus({ r: 0, c: i })}
                  onClick={() => setTab(t)}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: "8px 18px",
                    background: active
                      ? `linear-gradient(90deg, ${S.accent}, ${S.accent2})`
                      : "transparent",
                    color: active ? "#0a0d18" : S.ink,
                    fontWeight: active ? 600 : 500,
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    boxShadow:
                      focused && !active
                        ? `inset 0 0 0 1px ${S.accent}`
                        : "none",
                    transition: "all 180ms",
                  }}
                >
                  {t === "directory" ? "Directory" : "Welcome floor"}
                </button>
              );
            })}
          </div>
        </div>
        {tab === "directory" && <Directory />}
        {tab === "welcome" && <Welcome />}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

function Directory() {
  const S = useS();
  const { content } = useScheme();
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridAutoRows: "min-content",
        gap: 14,
        alignContent: "start",
        overflow: "hidden",
      }}
    >
      {content.infoSections.map((sec, i) => (
        <div
          key={sec.section}
          style={{
            padding: "18px 20px",
            background: S.glass,
            borderRadius: 16,
            border: `0.5px solid ${S.rule}`,
            backdropFilter: "blur(20px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              {sec.section}
            </div>
            <div
              style={{
                fontFamily: S.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                color: S.accent,
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {sec.items.map((it) => (
              <div
                key={it.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  fontSize: 12,
                }}
              >
                <span style={{ color: S.inkDim }}>{it.label}</span>
                <span style={{ textAlign: "right" }}>{it.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Welcome() {
  const S = useS();
  const floors = [
    {
      f: "Roof",
      t: "Rooftop garden · pavilion bar",
      tint: "rgba(124,108,255,0.40)",
    },
    { f: "6", t: "Suites 601-612", tint: "rgba(33,212,253,0.28)" },
    { f: "2-5", t: "Guest rooms · 84 keys", tint: "rgba(124,108,255,0.22)" },
    { f: "G", t: "Lobby · dining · library", tint: "rgba(255,110,156,0.30)" },
    { f: "B1", t: "Spa · pool · gym", tint: "rgba(33,212,253,0.30)" },
  ];
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr 380px",
        gap: 28,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          perspective: "1200px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            transform: "rotateX(58deg) rotateZ(-22deg)",
            transformStyle: "preserve-3d",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0,
          }}
        >
          {floors.map((fl, i) => (
            <div
              key={fl.f}
              style={{
                width: "70%",
                height: 64,
                marginTop: i === 0 ? 0 : -8,
                background: fl.tint,
                backdropFilter: "blur(8px)",
                border: "0.5px solid rgba(255,255,255,0.18)",
                borderRadius: 8,
                boxShadow:
                  "0 30px 60px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 24px",
                transform: "translateZ(0)",
                fontSize: 13,
                color: S.ink,
              }}
            >
              <span
                style={{
                  fontFamily: S.mono,
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                Level {fl.f}
              </span>
              <span
                style={{
                  color: "rgba(255,255,255,0.92)",
                  fontWeight: 500,
                }}
              >
                {fl.t}
              </span>
            </div>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            fontFamily: S.mono,
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: S.inkSf,
          }}
        >
          The house · 7 floors · 84 rooms
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          paddingTop: 4,
        }}
      >
        <div
          style={{
            fontFamily: S.mono,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: S.accent,
          }}
        >
          Welcome floor
        </div>
        <div
          style={{
            fontSize: 18,
            lineHeight: 1.5,
            color: S.ink,
            fontWeight: 500,
          }}
        >
          Make yourself comfortable. The lobby bar pours until 1 a.m.; the spa
          is quiet after nine; the rooftop garden opens at sunrise.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { t: "Wi-Fi", d: "NendaGuest · welcome2026" },
            { t: "In-room dining", d: "24 h · dial 3" },
            { t: "Spa & pool", d: "B1 · until 22:00" },
            { t: "Reception", d: "24 h · dial 0" },
          ].map((it) => (
            <div
              key={it.t}
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                background: S.glass,
                border: `0.5px solid ${S.rule}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 600 }}>{it.t}</span>
              <span
                style={{
                  color: S.inkDim,
                  fontFamily: S.mono,
                  fontSize: 12,
                }}
              >
                {it.d}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Radio({ go, scopeRef, context }: ScreenProps) {
  const S = useS();
  const stations = useScheme().content.stations;
  // Audio playback (start on OK / stop on leave); `playingId` null until OK.
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  const [focus, setFocus] = useFocusGrid({
    rows: stations.length,
    cols: 1,
    scopeRef,
    onEnter: (f) => toggle(stations[f.r]?.id),
    onBack: () => go("home"),
  });
  // The hero PREVIEWS the focused (hovered) station; OK starts playback.
  const station = stations[focus.r] ?? stations[0];
  const isPlaying = !!station && station.id === playingId;
  const np = useRadioNowPlaying(stations.map((s) => s.metadataId));
  const live = station?.metadataId ? np[station.metadataId] : undefined;
  const program = live?.program ?? station?.nowPlaying;
  const song = live?.song;
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [focus.r]);
  const hue = stationHue(station.id);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(closest-side at 28% 50%, hsla(${hue},80%,56%,0.30), transparent 50%)`,
          transition: "background 600ms",
        }}
      />
      <audio ref={audioRef} preload="none" />
      <Header crumb="Radio" />
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: 0,
          padding: "0 0 96px",
          minHeight: 0,
          position: "relative",
        }}
      >
        <div
          style={{
            padding: `0 ${PAGE_X}px`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontFamily: S.mono,
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: `hsl(${hue},90%,78%)`,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: isPlaying ? `hsl(${hue},90%,68%)` : "transparent",
                border: isPlaying ? "none" : `1.5px solid hsl(${hue},90%,68%)`,
                boxShadow: isPlaying ? `0 0 12px hsl(${hue},90%,68%)` : "none",
                animation: isPlaying ? "pulseGlow 1.6s infinite" : "none",
              }}
            />
            {isPlaying ? "On air · live" : "Preview"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: S.sans,
                fontWeight: 700,
                fontSize: 92,
                lineHeight: 0.92,
                letterSpacing: "-0.04em",
                background: `linear-gradient(180deg, #fff, hsl(${hue},80%,72%))`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {station.name}
            </h2>
            <span
              style={{
                fontFamily: S.mono,
                fontSize: 18,
                color: S.inkDim,
              }}
            >
              {station.freq}
            </span>
          </div>
          <div style={{ marginTop: 10, fontSize: 18, color: S.inkDim }}>
            {station.tag}
          </div>
          <div
            style={{
              marginTop: 36,
              display: "flex",
              alignItems: "center",
              gap: 3,
              height: 80,
            }}
          >
            {Array.from({ length: 80 }).map((_, i) => {
              const phase = i / 80;
              const h =
                8 +
                Math.abs(Math.sin(phase * Math.PI * 4 + hue) * 36) +
                (i % 3) * 6;
              return (
                <div
                  key={i}
                  style={{
                    width: 4,
                    height: h,
                    borderRadius: 2,
                    background: `linear-gradient(180deg, hsl(${hue},90%,72%), hsl(${(hue + 30) % 360},90%,52%))`,
                    opacity: 0.5 + (Math.sin(phase * Math.PI * 6) + 1) * 0.25,
                  }}
                />
              );
            })}
          </div>
          <div
            style={{
              marginTop: 28,
              display: "flex",
              alignItems: "center",
              gap: 18,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: `linear-gradient(135deg, hsl(${hue},85%,62%), hsl(${(hue + 40) % 360},80%,40%))`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 22,
                boxShadow: `0 8px 30px -6px hsla(${hue},85%,52%,0.6)`,
              }}
            >
              ▶
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: S.inkSf,
                  fontFamily: S.mono,
                }}
              >
                {isPlaying ? "Now playing" : "Preview"}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                {program}
              </div>
              {song && (
                <div
                  style={{
                    fontFamily: S.mono,
                    fontSize: 13,
                    color: S.inkDim,
                    marginTop: 2,
                  }}
                >
                  ♪ {song}
                </div>
              )}
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 4,
              }}
            >
              <div
                style={{
                  fontFamily: S.mono,
                  fontSize: 11,
                  color: S.inkSf,
                  letterSpacing: "0.1em",
                }}
              >
                LISTENING NOW
              </div>
              <div
                style={{
                  fontFamily: S.mono,
                  fontSize: 18,
                  color: S.ink,
                }}
              >
                {station.listeners}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "8px 36px 8px 0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "0 18px 10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 13,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: S.inkSf,
                fontFamily: S.mono,
              }}
            >
              Stations · {stations.length}
            </span>
          </div>
          <div
            className="foyer-scheme-scroll"
            style={{
              flex: 1,
              overflow: "auto",
              padding: "4px 18px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {stations.map((s, i) => {
                const active = focus.r === i;
                const rowPlaying = playingId === s.id;
                return (
                  <button
                    key={s.id}
                    ref={active ? rowRef : undefined}
                    onMouseEnter={() => setFocus({ r: i, c: 0 })}
                    onClick={() => toggle(s.id)}
                    style={{
                      appearance: "none",
                      border: 0,
                      padding: "10px 14px",
                      display: "grid",
                      gridTemplateColumns: "36px 1fr auto",
                      gap: 14,
                      alignItems: "center",
                      background: active ? "rgba(255,255,255,0.10)" : S.glass,
                      borderStyle: "solid",
                      borderWidth: "0.5px",
                      borderColor: active
                        ? `hsl(${stationHue(s.id)},80%,70%)`
                        : S.rule,
                      borderRadius: 12,
                      cursor: "pointer",
                      textAlign: "left",
                      color: S.ink,
                      fontFamily: "inherit",
                      backdropFilter: "blur(20px)",
                      boxShadow: active
                        ? `0 0 0 1.5px hsl(${stationHue(s.id)},80%,68%)`
                        : "none",
                      transition: "all 180ms",
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, hsl(${stationHue(s.id)},80%,62%), hsl(${(stationHue(s.id) + 40) % 360},78%,42%))`,
                        boxShadow: rowPlaying
                          ? `0 0 14px hsl(${stationHue(s.id)},90%,62%)`
                          : "none",
                      }}
                    />
                    <span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                          {s.name}
                        </span>
                        {rowPlaying && (
                          <span
                            style={{
                              fontFamily: S.mono,
                              fontSize: 9,
                              padding: "2px 6px",
                              borderRadius: 999,
                              background: `hsl(${stationHue(s.id)},80%,60%)`,
                              color: "#0a0d18",
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                            }}
                          >
                            LIVE
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: S.inkDim,
                          marginTop: 1,
                        }}
                      >
                        {s.tag}
                      </div>
                    </span>
                    <span
                      style={{
                        fontFamily: S.mono,
                        fontSize: 11,
                        color: S.inkSf,
                      }}
                    >
                      {s.freq}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

function HotelLaunch({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const S = useS();
  // The hotel screen's only action is to return home. Wire OK and the Back key
  // to it and keep the Back control focused so the remote can always escape.
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Hotel app" />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "0 80px 96px",
        }}
      >
        <div
          style={{
            fontFamily: S.mono,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: S.accent,
            marginBottom: 14,
          }}
        >
          ▸ Concierge · opening
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: S.sans,
            fontWeight: 700,
            fontSize: 96,
            lineHeight: 0.95,
            letterSpacing: "-0.03em",
            background: `linear-gradient(90deg, ${S.accent}, ${S.accent2})`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {context.hotelName}
        </h1>
        <p
          style={{
            marginTop: 16,
            fontSize: 16,
            color: S.inkDim,
            maxWidth: 640,
            lineHeight: 1.55,
          }}
        >
          Order to the room, book the spa, request housekeeping, settle the
          bill, or extend the stay. Everything the front desk can do - without
          picking up the phone.
        </p>
        <button
          onClick={() => go("home")}
          style={{
            marginTop: 36,
            appearance: "none",
            border: `1px solid ${S.accent}`,
            background: S.glassH,
            color: S.ink,
            padding: "10px 22px",
            borderRadius: 999,
            fontFamily: "inherit",
            fontSize: 13,
            cursor: "pointer",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            backdropFilter: "blur(20px)",
            boxShadow: `0 0 0 3px ${S.glow}`,
          }}
        >
          ← Back to home
        </button>
      </div>
    </div>
  );
}

/**
 * Screen Share (DEMO-1). Reached only as the FALLBACK: the "Screen Share" dest
 * tile tries `tryOpenScreenShare()` first and only calls `go("cast")` when
 * there is nothing to open (a set-top box entered from its own source list,
 * the CMS preview). So this page is pure instruction, never the casting UI
 * itself - same contract as `PageCast` in the launcher templates' shared kit,
 * ported here rather than imported so Spectrum doesn't reach into the other
 * template family for one screen.
 */
function Cast({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const S = useS();
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  const room = context.roomId ? `Room ${context.roomId}` : "Guest TV";
  const canCast = bridge.capabilities().guestCasting;
  const castInfo = bridge.getCastingInfo();
  const castName = castInfo.deviceName?.trim() || room;
  const steps = castSteps({
    info: canCast ? castInfo : null,
    deviceName: castName,
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Screen Share" />
      <div style={{ flex: 1, padding: `0 ${PAGE_X}px 96px`, minHeight: 0 }}>
        <h2
          style={{
            margin: "0 0 6px",
            fontFamily: S.sans,
            fontWeight: 600,
            fontSize: 44,
            letterSpacing: "-0.02em",
          }}
        >
          Share your{" "}
          <span
            style={{
              background: `linear-gradient(90deg, ${S.accent}, ${S.accent2})`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            screen.
          </span>
        </h2>
        <p style={{ margin: "0 0 24px", fontSize: 14, color: S.inkDim }}>
          Mirror your phone, tablet or laptop onto this television.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 14,
          }}
        >
          {steps.map((s) => (
            <div
              key={s.n}
              style={{
                background: S.glass,
                border: `0.5px solid ${S.rule}`,
                backdropFilter: "blur(20px)",
                borderRadius: 16,
                padding: "24px 22px",
              }}
            >
              <div
                style={{
                  fontFamily: S.sans,
                  fontSize: 28,
                  fontWeight: 600,
                  color: S.accent,
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontFamily: S.sans,
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: 13.5, color: S.inkDim, marginTop: 6 }}>
                {s.detail}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
